// src/utils/publicEvents.js
//
// Helpers de la billetterie des événements publics (workshops / stages).
// Partagés par EventForm.js, EventEdit.js, Events.js et EventDetails.js.
//
// Modèle Firestore (events/{id}) — champs ajoutés :
//   isPublic, status ('draft' | 'published' | 'cancelled'), slug,
//   title_i18n, description_i18n, imageUrl, imagePath, instagramUrl,
//   location { name, address }, level, ageMin, ageMax, whatToBring_i18n,
//   registrationDeadline, ticketTypes[ { id, name_i18n, description_i18n,
//   price, quantity (null = illimité), salesEnd, active } ]
//
// ⚠️ ticketStock ({ [ticketId]: { sold, reserved } }) est écrit UNIQUEMENT
// par les Cloud Functions (eventOrders.ts). L'admin ne l'envoie jamais :
// les règles Firestore refusent toute mise à jour qui le touche.

import { Timestamp, collection, getDocs, query, where } from 'firebase/firestore';

export const LANGS = ['fr', 'en', 'nl'];
export const LANG_LABELS = { fr: 'Français', en: 'English', nl: 'Nederlands' };

// Valeurs FR, identiques à celles des cours (le site traduit à l'affichage).
export const LEVELS = ['Tous niveaux', 'Débutant', 'Intermédiaire', 'Avancé'];

export const PUBLIC_STATUS = {
  draft:     { label: 'Brouillon', color: 'default' },
  published: { label: 'En ligne',  color: 'success' },
  cancelled: { label: 'Annulé',    color: 'error' },
};

export const ORDER_STATUS = {
  paid:             { label: 'Payée',                 bg: '#e8f5e9', color: '#2e7d32' },
  reserved:         { label: 'Paiement en cours',     bg: '#fff3e0', color: '#e65100' },
  awaiting_payment: { label: 'Relancée — en attente', bg: '#fff8e1', color: '#b45309' },
  abandoned:        { label: 'Abandonnée',            bg: '#eceff1', color: '#455a64' },
  cancelled:        { label: 'Annulée',               bg: '#eceff1', color: '#455a64' },
};

// Commandes qui bloquent une place (ou qui sont payées).
export const ACTIVE_ORDER_STATUSES = ['paid', 'reserved', 'awaiting_payment'];

export const SITE_BASE = 'https://mkdstudio.co';

// ─── Petits helpers ────────────────────────────────────────────────────────

export const emptyI18n = () => ({ fr: '', en: '', nl: '' });

export const i18nFrom = (value, fallbackFr = '') => {
  if (value && typeof value === 'object') {
    return { fr: value.fr || fallbackFr || '', en: value.en || '', nl: value.nl || '' };
  }
  return { fr: (typeof value === 'string' ? value : '') || fallbackFr || '', en: '', nl: '' };
};

// Ne stocke que les langues remplies (le site retombe sur le FR sinon).
export const compactI18n = (obj) => {
  const out = {};
  LANGS.forEach((l) => {
    const v = (obj?.[l] || '').trim();
    if (v) out[l] = v;
  });
  return Object.keys(out).length ? out : null;
};

export const money = (value) => {
  const n = Number(value || 0);
  return `${n.toFixed(Number.isInteger(n) ? 0 : 2)} €`;
};

export const readDate = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const pad = (n) => String(n).padStart(2, '0');

// Timestamp → valeur d'un <input type="datetime-local"> (heure locale).
export const tsToLocalInput = (value) => {
  const d = readDate(value);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// <input type="datetime-local"> (heure locale) → Timestamp | null.
export const localInputToTs = (value) => {
  if (!value) return null;
  const d = new Date(value); // "YYYY-MM-DDTHH:mm" sans fuseau = heure locale
  return Number.isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
};

// "YYYY-MM-DD" + "HH:mm" en heure LOCALE (évite new Date('YYYY-MM-DD'),
// interprété en UTC par le navigateur).
export const combineLocalDateTime = (dayISO, hhmm = '00:00') => {
  const [y, m, d] = String(dayISO || '').split('-').map(Number);
  const [h, mi] = String(hhmm || '00:00').split(':').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, h || 0, mi || 0, 0, 0);
};

export const slugify = (str) =>
  String(str || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' et ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const suggestSlug = (title, startDate) => {
  const base = slugify(title);
  const year = String(startDate || '').slice(0, 4);
  if (!base) return '';
  return year && !base.includes(year) ? `${base}-${year}` : base;
};

export async function isSlugTaken(db, slug, excludeId = null) {
  if (!slug) return false;
  const snap = await getDocs(query(collection(db, 'events'), where('slug', '==', slug)));
  return snap.docs.some((d) => d.id !== excludeId);
}

export const newTicketId = () => `t_${Math.random().toString(36).slice(2, 10)}`;

export const emptyTicket = (overrides = {}) => ({
  id: newTicketId(),
  name_i18n: emptyI18n(),
  description_i18n: emptyI18n(),
  price: '',
  quantity: '',
  salesEnd: '',
  active: true,
  isNew: true,
  ...overrides,
});

// ─── Formulaire ↔ document ─────────────────────────────────────────────────

export const defaultPublicForm = () => ({
  isPublic: false,
  status: 'draft',
  slug: '',
  slugTouched: false,
  title_en: '',
  title_nl: '',
  description_en: '',
  description_nl: '',
  imageUrl: '',
  imagePath: '',
  instagramUrl: '',
  locationName: 'MKD Studio',
  locationAddress: '',
  level: 'Tous niveaux',
  ageMin: '',
  ageMax: '',
  whatToBring: emptyI18n(),
  registrationDeadline: '',
  ticketTypes: [emptyTicket({ name_i18n: { fr: 'Standard', en: 'Standard', nl: 'Standaard' } })],
});

export function publicFormFromEvent(ev = {}) {
  const tickets = Array.isArray(ev.ticketTypes) ? ev.ticketTypes : [];
  return {
    isPublic: ev.isPublic === true,
    status: ev.status || 'draft',
    slug: ev.slug || '',
    slugTouched: !!ev.slug,
    title_en: ev.title_i18n?.en || '',
    title_nl: ev.title_i18n?.nl || '',
    description_en: ev.description_i18n?.en || '',
    description_nl: ev.description_i18n?.nl || '',
    imageUrl: ev.imageUrl || '',
    imagePath: ev.imagePath || '',
    instagramUrl: ev.instagramUrl || '',
    locationName: ev.location?.name ?? 'MKD Studio',
    locationAddress: ev.location?.address || '',
    level: ev.level || 'Tous niveaux',
    ageMin: ev.ageMin ?? '',
    ageMax: ev.ageMax ?? '',
    whatToBring: i18nFrom(ev.whatToBring_i18n),
    registrationDeadline: tsToLocalInput(ev.registrationDeadline),
    ticketTypes: tickets.length
      ? tickets.map((t) => ({
          id: t.id,
          name_i18n: i18nFrom(t.name_i18n, t.name),
          description_i18n: i18nFrom(t.description_i18n),
          price: t.price ?? '',
          quantity: t.quantity === null || t.quantity === undefined ? '' : t.quantity,
          salesEnd: tsToLocalInput(t.salesEnd),
          active: t.active !== false,
          isNew: false,
        }))
      : defaultPublicForm().ticketTypes,
  };
}

// Places vendues / réservées d'un ticket (lu sur ticketStock).
export const stockOf = (ticketStock, ticketId) => {
  const s = ticketStock?.[ticketId] || {};
  return { sold: Number(s.sold || 0), reserved: Number(s.reserved || 0) };
};

/**
 * Valide la section publication. Lève une Error au 1er problème.
 * Un brouillon est peu contrôlé ; un événement "En ligne" doit être complet.
 */
export function validatePublicForm(pf, { title, endDateTime, ticketStock }) {
  if (!pf.isPublic) return;
  const published = pf.status === 'published';

  if (!SLUG_RE.test(pf.slug || '')) {
    throw new Error("L'adresse de la page (slug) est invalide : lettres minuscules, chiffres et tirets uniquement.");
  }
  if (pf.instagramUrl && !/^https:\/\/(www\.)?instagram\.com\//i.test(pf.instagramUrl.trim())) {
    throw new Error('Le lien Instagram doit commencer par https://www.instagram.com/');
  }

  const ageMin = pf.ageMin === '' ? null : Number(pf.ageMin);
  const ageMax = pf.ageMax === '' ? null : Number(pf.ageMax);
  if ((ageMin !== null && (!Number.isInteger(ageMin) || ageMin < 0 || ageMin > 99))
    || (ageMax !== null && (!Number.isInteger(ageMax) || ageMax < 0 || ageMax > 99))) {
    throw new Error("Les âges minimum / maximum doivent être des nombres entiers.");
  }
  if (ageMin !== null && ageMax !== null && ageMin > ageMax) {
    throw new Error("L'âge minimum ne peut pas dépasser l'âge maximum.");
  }

  const deadline = pf.registrationDeadline ? new Date(pf.registrationDeadline) : null;
  if (deadline && endDateTime && deadline > endDateTime) {
    throw new Error("La date limite d'inscription doit être avant la fin de l'événement.");
  }

  const ids = new Set();
  pf.ticketTypes.forEach((t, i) => {
    const label = t.name_i18n?.fr?.trim() || `Ticket ${i + 1}`;
    if (ids.has(t.id)) throw new Error(`Identifiant de ticket en double (${label}).`);
    ids.add(t.id);
    if (!t.name_i18n?.fr?.trim()) throw new Error(`Le ticket ${i + 1} n'a pas de nom (FR).`);

    const price = Number(t.price);
    if (t.price === '' || !Number.isFinite(price) || price < 0) {
      throw new Error(`Prix invalide pour « ${label} ».`);
    }
    if (price > 0 && price < 1) {
      throw new Error(`Le prix de « ${label} » doit être 0 (gratuit) ou au moins 1 €.`);
    }
    if (t.quantity !== '') {
      const q = Number(t.quantity);
      if (!Number.isInteger(q) || q < 0) throw new Error(`Nombre de places invalide pour « ${label} ».`);
      const { sold, reserved } = stockOf(ticketStock, t.id);
      if (q < sold + reserved) {
        throw new Error(`« ${label} » : ${sold + reserved} place(s) déjà vendue(s) ou réservée(s), impossible de descendre à ${q}.`);
      }
    }
    if (t.salesEnd && endDateTime && new Date(t.salesEnd) > endDateTime) {
      throw new Error(`La fin des ventes de « ${label} » doit être avant la fin de l'événement.`);
    }
  });

  if (published) {
    if (!pf.imageUrl) throw new Error("Ajoutez une image avant de mettre l'événement en ligne.");
    if (!pf.ticketTypes.some((t) => t.active)) {
      throw new Error("Au moins un ticket actif est nécessaire pour mettre l'événement en ligne.");
    }
    if (!title?.trim()) throw new Error('Le titre est obligatoire.');
  }
}

/**
 * Construit les champs Firestore de la section publication.
 * N'inclut JAMAIS ticketStock.
 */
export function buildPublicPayload(pf, { title, description }) {
  if (!pf.isPublic) {
    return { isPublic: false };
  }

  const ticketTypes = pf.ticketTypes.map((t, i) => {
    const name = compactI18n(t.name_i18n) || { fr: `Ticket ${i + 1}` };
    return {
      id: t.id,
      name: name.fr,
      name_i18n: name,
      description_i18n: compactI18n(t.description_i18n),
      price: Math.round(Number(t.price) * 100) / 100,
      quantity: t.quantity === '' ? null : Number(t.quantity),
      salesEnd: localInputToTs(t.salesEnd),
      active: !!t.active,
      order: i,
    };
  });

  const activePrices = ticketTypes.filter((t) => t.active).map((t) => t.price);

  return {
    isPublic: true,
    status: pf.status,
    slug: pf.slug,
    title_i18n: compactI18n({ fr: title, en: pf.title_en, nl: pf.title_nl }),
    description_i18n: compactI18n({ fr: description, en: pf.description_en, nl: pf.description_nl }),
    imageUrl: pf.imageUrl || null,
    imagePath: pf.imagePath || null,
    instagramUrl: pf.instagramUrl.trim() || null,
    location: { name: pf.locationName.trim(), address: pf.locationAddress.trim() },
    level: pf.level || null,
    ageMin: pf.ageMin === '' ? null : Number(pf.ageMin),
    ageMax: pf.ageMax === '' ? null : Number(pf.ageMax),
    whatToBring_i18n: compactI18n(pf.whatToBring),
    registrationDeadline: localInputToTs(pf.registrationDeadline),
    ticketTypes,
    // Champ historique "price" (liste admin / app mobile) : prix d'appel.
    price: activePrices.length ? Math.min(...activePrices) : 0,
  };
}

// ─── Statistiques de vente ─────────────────────────────────────────────────

export function ticketSalesSummary(ev = {}) {
  const tickets = Array.isArray(ev.ticketTypes) ? ev.ticketTypes : [];
  let sold = 0;
  let reserved = 0;
  let capacity = 0;
  let unlimited = false;
  tickets.forEach((t) => {
    const s = stockOf(ev.ticketStock, t.id);
    sold += s.sold;
    reserved += s.reserved;
    if (t.quantity === null || t.quantity === undefined) unlimited = true;
    else capacity += Number(t.quantity) || 0;
  });
  return { sold, reserved, capacity: unlimited ? null : capacity };
}

export const minTicketPrice = (ev = {}) => {
  const prices = (Array.isArray(ev.ticketTypes) ? ev.ticketTypes : [])
    .filter((t) => t.active !== false)
    .map((t) => Number(t.price) || 0);
  return prices.length ? Math.min(...prices) : null;
};

export const publicEventUrl = (ev) => (ev?.slug ? `${SITE_BASE}/evenements/${ev.slug}` : null);

// Âge (années révolues) à une date donnée ; dob = "YYYY-MM-DD".
export const ageAt = (dobISO, date) => {
  if (!dobISO || !date) return null;
  const [y, m, d] = String(dobISO).split('-').map(Number);
  if (!y) return null;
  let age = date.getFullYear() - y;
  if (date.getMonth() + 1 < m || (date.getMonth() + 1 === m && date.getDate() < d)) age--;
  return age;
};
